import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Component, Input, OnInit, OnChanges, SimpleChanges, forwardRef, EventEmitter, Output } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { CalenderInputComponent } from '../calender-input/calender-input.component';
import { ResponseNotificationService } from '../../services/response-notification/response-notification.service';

@Component({
  selector: 'app-date-picker',
  templateUrl: './custom-date-picker.component.html',
  styleUrls: ['./custom-date-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDatePickerComponent),
      multi: true
    }
  ]
})
export class CustomDatePickerComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() minDate: Date | null = null; // Minimum date
  @Input() maxDate: Date | null = null; // Maximum date

  @Input() selectedDate: Date | null = null;
  manualDateInput: string = '';
  day: string = '';
  month: string = '';
  year: string = '';

  // ControlValueAccessor methods
  @Input() isDisabled: boolean = false;

  private onChange: (value: Date) => void = () => { };
  private onTouched: () => void = () => { };
  @Output() change: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private _popoverController: PopoverController,
    private _responseNotificationService: ResponseNotificationService
  ) { }

  ngOnInit() {
  }

ngOnChanges(changes: SimpleChanges) {
  if (changes['selectedDate']) {
    const newValue = changes['selectedDate'].currentValue;
    const previousValue = changes['selectedDate'].previousValue;
    if (newValue !== previousValue) {
      this.patchDate(newValue ?? null);
    }
  }
}
  onInputChange(field: 'day' | 'month' | 'year', event: Event): void {
    this.validateAndSetDate();
  }

  validateAndSetDate(): void {
    if(!this.day || !this.month || !this.year){
      return;
    }
    const day = parseInt(this.day, 10);
    const month = parseInt(this.month, 10) - 1; // Months are 0-indexed
    const year = parseInt(this.year, 10);

    if (this.isValidDate(day, month, year)) {
      const date = new Date(year, month, day);
      if (this.isWithinRange(date)) {
        this.onDateChange(date);
      } else {
        this.resetInputs('Date is out of range.');
      }
    } else {
      this.resetInputs('Invalid date format.');
    }
  }

  resetInputs(message: string): void {
    this._responseNotificationService.showMessage(message, true);
    this.day = '';
    this.month = '';
    this.year = '';
  }

  isValidDate(day: number, month: number, year: number): boolean {
    if (day <= 0 || month < 0 || month > 11 || year <= 0) {
      return false;
    }
    const date = new Date(year, month, day);
    return (
      date.getFullYear() === year &&
      date.getMonth() === month &&
      date.getDate() === day
    );
  }

  isWithinRange(date: Date): boolean {
    return (
      (!this.minDate || date >= this.minDate) &&
      (!this.maxDate || date <= this.maxDate)
    );
  }

  // Calendar methods
  async openCalendar(event: any) {
    const change = new EventEmitter<any>();
    const popover = await this._popoverController.create({
      component: CalenderInputComponent,
      event: event,
      cssClass: 'auto-width-height-popover-component',
      componentProps: {
        selectedDate: this.selectedDate,
        minDate: this.minDate,
        maxDate: this.maxDate,
        change: change
      }
    });

    change.subscribe((date: any) => {
      this.onDateChange(date);
      this.patchDate(this.selectedDate);
    })
    return await popover.present();
  }

  patchDate(date:any){
    if(date){
      this.day = date.getDate().toString().padStart(2, '0');
      this.month = (date.getMonth() + 1).toString().padStart(2, '0');
      this.year = date.getFullYear().toString();
    }else{
      this.day = "";
      this.month = "";
      this.year = "";
    }
  }

  // ControlValueAccessor methods
  writeValue(value: Date): void {
    this.selectedDate = value ? new Date(value) : null;
    this.patchDate(this.selectedDate);

  }

  registerOnChange(fn: (value: Date) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  onDateChange(value: Date | null): void {
    if (this.selectedDate !== value) {
      this.selectedDate = this.setTimeToZero(value);
      this.onChange(this.selectedDate);
      this.onTouched();
      this.change.emit(this.selectedDate);
    }
  }

  setTimeToZero(date: Date | null): Date {
    if (!date) {
      return null!;
    }
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  }
}
